import React from 'react';
import { Box, Stack, Typography, Button, Tooltip, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DraggableDialog from './DraggableDialog';
import GoldenArrow from './GoldenArrow';
import apiService from '../../services/api.service';

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

type Props = {
  open: boolean;
  onClose: () => void;
  context: 'inbox' | 'inprogress' | 'completed' | 'all';
};

const PRGlobalPicker: React.FC<Props> = ({ open, onClose, context }) => {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, approverRes, pendingRes] = await Promise.all([
        apiService.getMyPRs(),
        apiService.getApproverPRs(),
        apiService.getPendingApprovals(),
      ]);
      const myList = (myRes as any)?.data || myRes || [];
      const apprList = (approverRes as any)?.data || approverRes || [];
      const pendingList = (pendingRes as any)?.data || pendingRes || [];
      let list: any[] = [];
      if (context === 'inbox') {
        const myRejected = myList.filter((r: any) => String(r.status || '').toLowerCase() === 'rejected');
        const apprRejected = apprList.filter((r: any) => String(r.status || '').toLowerCase() === 'rejected');
        list = [...pendingList, ...myRejected, ...apprRejected];
        const seen = new Set<number>();
        list = list.filter((r: any) => { const id = Number(r.prid || r.PRID || 0); if (!id || seen.has(id)) return false; seen.add(id); return true; });
        list = list.filter((r: any) => { const st = String(r.status || '').toLowerCase(); const posted = st === 'postedtosap/pr' || Number(r.sappodocnum || r.SAPPODocNum || 0) > 0; const approved = st === 'approved'; return !approved && !posted; });
      } else if (context === 'inprogress') {
        list = [...myList, ...apprList];
        const seen = new Set<number>();
        list = list.filter((r: any) => { const id = Number(r.prid || r.PRID || 0); if (!id || seen.has(id)) return false; seen.add(id); return true; });
        list = list.filter((r: any) => { const st = String(r.status || '').toLowerCase(); const inbox = String(r.inboxstatus || '').toLowerCase(); const posted = st === 'postedtosap/pr' || Number(r.sappodocnum || r.SAPPODocNum || 0) > 0; const isPending = st.startsWith('pending'); const finalApprovedNotPosted = st === 'approved' && !posted; const inProgressInbox = inbox === 'in progress' && !posted; const rejected = st === 'rejected'; return (isPending || finalApprovedNotPosted || inProgressInbox) && !rejected; });
      } else if (context === 'completed') {
        list = [...myList, ...apprList];
        const seen = new Set<number>();
        list = list.filter((r: any) => { const id = Number(r.prid || r.PRID || 0); if (!id || seen.has(id)) return false; seen.add(id); return true; });
        list = list.filter((r: any) => { const st = String(r.status || '').toLowerCase(); const inbox = String(r.inboxstatus || '').toLowerCase(); const posted = st === 'postedtosap/pr' || Number(r.sappodocnum || r.SAPPODocNum || 0) > 0 || Boolean((r as any).convertedtopo); const fullyApproved = st === 'approved'; const isCompletedInbox = inbox === 'completed'; const rejected = st === 'rejected'; const pending = st.startsWith('pending'); return (posted || fullyApproved || isCompletedInbox) && !pending && (!rejected || isCompletedInbox); });
      } else {
        list = [...pendingList, ...myList, ...apprList];
        const seen = new Set<number>();
        list = list.filter((r: any) => { const id = Number(r.prid || r.PRID || 0); if (!id || seen.has(id)) return false; seen.add(id); return true; });
        // No status filtering in 'all' context; show everything accessible
      }
      const withIds = list.map((r: any, i: number) => ({ id: r.prid ?? i, ...r }));
      setRows(withIds);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [context]);

  React.useEffect(() => { if (open) { void load(); } }, [open, load]);

  const filteredRows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    const tokens = q.split(/\s+/).filter(Boolean);
    return rows.filter((r: any) => {
      const f = (name: string) => String((r as any)[name] ?? '').toLowerCase();
      const fields = [
        f('prnumber'),
        f('requisitiontype'),
        f('allocationtype'),
        f('allocationcode'),
        f('status'),
        f('requireddate'),
        f('currency'),
        f('requestedbyname'),
        f('RequestedByName'),
        f('requestedbyemail'),
        f('RequestedByEmail'),
        f('prdate'),
        f('PRDate'),
        f('remarks'),
        f('sappodocnum'),
        f('SAPPODocNum'),
        f('sappodocentry'),
        f('SAPPODocEntry'),
        String((r.totalamount ?? (r.TotalAmount ?? ''))).toLowerCase(),
        String((r.prid ?? r.PRID ?? '')).toLowerCase(),
      ].filter((v) => v && v.length > 0);
      return tokens.every((t) => fields.some((v) => v.includes(t)));
    });
  }, [rows, query]);

  const columns: GridColDef[] = React.useMemo(() => ([
    {
      field: 'prnumber',
      headerName: 'PR No.',
      width: 260,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Tooltip title={`Type: ${params.row.requisitiontype} • Allocation: ${params.row.allocationtype}/${params.row.allocationcode} • Needed: ${toDDMMYYYY(params.row.requireddate)} • Total: ${Number(params.row.totalamount||0).toFixed(2)} ${params.row.currency}`} enterDelay={400}>
            <Box sx={{ flexGrow: 1, minWidth: 0, px: 1, borderRadius: 1, bgcolor: params.row.sappodocnum ? '#E3F2FD' : 'transparent', color: params.row.sappodocnum ? '#0D47A1' : 'inherit' }}>
              <Typography variant="body2" noWrap>{params.value}{params.row.sappodocnum ? ` • SAP DocNum ${params.row.sappodocnum}` : ''}</Typography>
            </Box>
          </Tooltip>
          <Box sx={{ flexShrink: 0 }}>
            <GoldenArrow prId={Number(params.row.prid)} />
          </Box>
        </Stack>
      )
    },
    { field: 'requisitiontype', headerName: 'Type', width: 140 },
    { field: 'allocationtype', headerName: 'Allocation', width: 140 },
    { field: 'allocationcode', headerName: 'Code', width: 180 },
    { field: 'status', headerName: 'Status', width: 200 },
    { field: 'requireddate', headerName: 'Needed By', width: 140, valueFormatter: (p) => toDDMMYYYY(p.value) },
    { field: 'totalamount', headerName: 'Total', width: 140, valueFormatter: (p) => Number(p.value || 0).toFixed(2) },
    { field: 'currency', headerName: 'Currency', width: 100 },
  ]), []);

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title="Search Purchase Requisition"
      fullWidth
      maxWidth="lg"
      actions={<Button onClick={onClose}>Close</Button>}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ flexShrink: 0 }}>Showing PRs for: {context === 'inbox' ? 'Inbox' : context === 'inprogress' ? 'In Progress' : context === 'completed' ? 'Completed' : 'All'}</Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search PR No., SAP DocNum, status, requester, allocation, etc."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      <Box sx={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={filteredRows}
          columns={columns}
          density="compact"
          pageSizeOptions={[50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
          disableRowSelectionOnClick={false}
          onRowClick={(params) => { try { window.dispatchEvent(new CustomEvent('pr-select', { detail: { id: Number(params.row.prid || params.id || 0) } })); } catch {}; onClose(); }}
          sx={{ width: '100%' }}
        />
      </Box>
    </DraggableDialog>
  );
};

export default PRGlobalPicker;
